import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';

import { SavedReport } from './ReportTabs.props';

export interface SetDefaultConfirmDialogProps {
  /** Report being acted on; `null` keeps the dialog closed. */
  report: SavedReport | null;
  /** Whether we're setting a new default or removing one. */
  action: 'set' | 'remove';
  /** Current default report (used to phrase the "this will replace…" message). */
  currentDefault: SavedReport | undefined;
  onCancel: () => void;
  onConfirm: () => void;
}

export const SetDefaultConfirmDialog: React.FC<SetDefaultConfirmDialogProps> = ({
  report,
  action,
  currentDefault,
  onCancel,
  onConfirm,
}) => {
  const theme = useTheme();

  const message = (() => {
    if (!report) return '';
    if (action === 'remove') {
      return `Remove "${report.name}" as your default report?`;
    }
    if (currentDefault && currentDefault.code !== report.code) {
      return `This will replace "${currentDefault.name}" as your default report. Continue?`;
    }
    return `Set "${report.name}" as your default report?`;
  })();

  const title = action === 'remove' ? 'Remove Default' : 'Set as Default';

  return (
    <Dialog open={report !== null} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
